import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, Certification } from '../../lib/supabase';
import { DocumentUpload } from './DocumentUpload';
import { getExpirationStatus } from '../../lib/documentService';
import {
  Plus,
  FileText,
  Calendar,
  Shield,
  ExternalLink,
  Trash2,
  Edit2,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Award
} from 'lucide-react';

export const CertificationsManager: React.FC = () => {
  const { profile } = useAuth();
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    type: 'license' as 'license' | 'certification' | 'skill',
    name: '',
    issuer: '',
    number: '',
    issue_date: '',
    expiry_date: '',
    document_url: ''
  });

  useEffect(() => {
    loadCertifications();
  }, [profile]);

  const loadCertifications = async () => {
    if (!profile) return;

    try {
      const { data, error } = await supabase
        .from('certifications')
        .select('*')
        .eq('user_id', profile.id)
        .order('expiry_date', { ascending: true });

      if (error) throw error;
      setCertifications(data || []);
    } catch (error) {
      console.error('Error loading certifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;

    try {
      if (editingId) {
        const { error } = await supabase
          .from('certifications')
          .update({
            ...formData,
            verified: false
          })
          .eq('id', editingId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('certifications')
          .insert({
            user_id: profile.id,
            ...formData,
            verified: false
          });

        if (error) throw error;
      }

      resetForm();
      await loadCertifications();
    } catch (error) {
      console.error('Error saving certification:', error);
      alert('Failed to save certification');
    }
  };

  const handleEdit = (cert: Certification) => {
    setFormData({
      type: cert.type,
      name: cert.name,
      issuer: cert.issuer || '',
      number: cert.number || '',
      issue_date: cert.issue_date || '',
      expiry_date: cert.expiry_date || '',
      document_url: cert.document_url || ''
    });
    setEditingId(cert.id);
    setShowAddForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this certification?')) return;

    try {
      const { error } = await supabase
        .from('certifications')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await loadCertifications();
    } catch (error) {
      console.error('Error deleting certification:', error);
      alert('Failed to delete certification');
    }
  };

  const handleUploadComplete = async (url: string, certId?: string) => {
    if (certId) {
      try {
        const { error } = await supabase
          .from('certifications')
          .update({ document_url: url })
          .eq('id', certId);

        if (error) throw error;
        setUploadingFor(null);
        await loadCertifications();
      } catch (error) {
        console.error('Error updating document URL:', error);
      }
    } else {
      setFormData(prev => ({ ...prev, document_url: url }));
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'license',
      name: '',
      issuer: '',
      number: '',
      issue_date: '',
      expiry_date: '',
      document_url: ''
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'license':
        return <Shield className="w-5 h-5" />;
      case 'certification':
        return <Award className="w-5 h-5" />;
      case 'skill':
        return <CheckCircle2 className="w-5 h-5" />;
      default:
        return <FileText className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'license':
        return 'bg-blue-100 text-blue-700';
      case 'certification':
        return 'bg-green-100 text-green-700';
      case 'skill':
        return 'bg-amber-100 text-amber-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Licenses & Certifications</h2>
          <p className="text-slate-600 mt-1">Manage your professional credentials</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="flex items-center px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Credential
        </button>
      </div>

      {showAddForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900">
              {editingId ? 'Edit Credential' : 'Add New Credential'}
            </h3>
            <button
              onClick={resetForm}
              className="text-slate-400 hover:text-slate-600"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                  required
                >
                  <option value="license">License</option>
                  <option value="certification">Certification</option>
                  <option value="skill">Skill</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Electrical License, OSHA 30"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Issuing Organization
                </label>
                <input
                  type="text"
                  value={formData.issuer}
                  onChange={(e) => setFormData({ ...formData, issuer: e.target.value })}
                  placeholder="e.g., State Board, OSHA"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  License/Certificate Number
                </label>
                <input
                  type="text"
                  value={formData.number}
                  onChange={(e) => setFormData({ ...formData, number: e.target.value })}
                  placeholder="Certificate ID"
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Issue Date
                </label>
                <input
                  type="date"
                  value={formData.issue_date}
                  onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Expiration Date
                </label>
                <input
                  type="date"
                  value={formData.expiry_date}
                  onChange={(e) => setFormData({ ...formData, expiry_date: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:border-slate-400 focus:ring-2 focus:ring-slate-100 outline-none"
                />
              </div>
            </div>

            {!formData.document_url && profile && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Upload Document (Optional)
                </label>
                <DocumentUpload
                  userId={profile.id}
                  documentType="certification"
                  onUploadComplete={(url) => handleUploadComplete(url)}
                />
              </div>
            )}

            {formData.document_url && (
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-700">Document uploaded</span>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, document_url: '' })}
                  className="text-sm text-green-600 hover:text-green-700"
                >
                  Remove
                </button>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                className="flex-1 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg transition-colors"
              >
                {editingId ? 'Update' : 'Add'} Credential
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2.5 border border-slate-200 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {certifications.map((cert) => {
          const expiryStatus = cert.expiry_date ? getExpirationStatus(cert.expiry_date) : null;

          return (
            <div key={cert.id} className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4 flex-1">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getTypeColor(cert.type)}`}>
                    {getTypeIcon(cert.type)}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-bold text-slate-900">{cert.name}</h3>
                      {cert.verified && (
                        <CheckCircle2 className="w-4 h-4 text-green-600" title="Verified" />
                      )}
                    </div>

                    {cert.issuer && (
                      <p className="text-sm text-slate-600 mb-2">{cert.issuer}</p>
                    )}

                    <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                      {cert.number && (
                        <span className="flex items-center">
                          <FileText className="w-4 h-4 mr-1" />
                          {cert.number}
                        </span>
                      )}
                      {cert.issue_date && (
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          Issued: {new Date(cert.issue_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>

                    {expiryStatus && (
                      <div className={`inline-flex items-center mt-2 px-2.5 py-1 rounded-full text-xs font-medium ${
                        expiryStatus.status === 'expired'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : expiryStatus.status === 'expiring'
                          ? 'bg-amber-50 text-amber-700 border border-amber-200'
                          : 'bg-green-50 text-green-700 border border-green-200'
                      }`}>
                        {expiryStatus.status === 'expired' ? (
                          <XCircle className="w-3 h-3 mr-1" />
                        ) : expiryStatus.status === 'expiring' ? (
                          <AlertTriangle className="w-3 h-3 mr-1" />
                        ) : (
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                        )}
                        {expiryStatus.message}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  {cert.document_url ? (
                    <a
                      href={cert.document_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="View document"
                    >
                      <ExternalLink className="w-4 h-4 text-slate-600" />
                    </a>
                  ) : (
                    <button
                      onClick={() => setUploadingFor(cert.id)}
                      className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                      title="Upload document"
                    >
                      <FileText className="w-4 h-4 text-slate-600" />
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(cert)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4 text-slate-600" />
                  </button>
                  <button
                    onClick={() => handleDelete(cert.id)}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              </div>

              {uploadingFor === cert.id && profile && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <DocumentUpload
                    userId={profile.id}
                    documentType="certification"
                    onUploadComplete={(url) => handleUploadComplete(url, cert.id)}
                    onCancel={() => setUploadingFor(null)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {certifications.length === 0 && !showAddForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Award className="w-8 h-8 text-slate-400" />
          </div>
          <h3 className="font-semibold text-slate-900 mb-2">No credentials yet</h3>
          <p className="text-slate-600 mb-4">
            Add your licenses, certifications, and skills to stand out
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-lg transition-colors"
          >
            Add First Credential
          </button>
        </div>
      )}
    </div>
  );
};
